import React, { useState } from 'react';
import { toast, Card } from '../V2Components';
import FieldErrorList from '../Electrical/FieldErrorList';

// Client-side mirror of the backend StructureType enum (models/enums.py).
// Values must match the enum string values exactly — Pydantic does exact-value
// validation, so any drift 400s a save. The backend test
// test_structure_enum_values_match_frontend_contract pins them.
export const STRUCTURE_TYPES = ['Roof', 'Foundation', 'Siding'];

// Curated material suggestions per structure type. These are only autocomplete
// hints (rendered via a <datalist>) — `material` is a free-text column, so a
// user can type anything. Drawn from the RoofType / ExteriorType /
// FoundationType RentCast vocabularies.
const MATERIAL_SUGGESTIONS = {
    Roof: ['Asphalt Shingle', 'Metal', 'Slate', 'Tile', 'Wood Shake', 'Composition Shingle', 'Flat / Built-up'],
    Foundation: ['Poured Concrete', 'Concrete Block', 'Slab', 'Crawl Space', 'Basement', 'Pier & Beam', 'Stone'],
    Siding: ['Vinyl', 'Aluminum', 'Wood', 'Fiber Cement', 'Brick', 'Stucco', 'Stone Veneer', 'Composite'],
};

const EMPTY_FORM = {
    structure_type: 'Roof',
    name: '',
    material: '',
    install_date: '',
    warranty_expiration: '',
    contractor: '',
    notes: '',
};

/**
 * Create/edit form for a Structure, rendered as an inline in-page card
 * (matching the Add Utility / PanelForm pattern) — the form sits in the page
 * flow below the tab header, no overlay. Returns null when closed; the parent
 * (StructuresTab) owns open/close state and the API call.
 *
 * Modes disambiguated by `structure.id` (like PanelForm).
 *
 * Props:
 *   isOpen  - boolean
 *   onClose - () => void
 *   onSave  - (payload) => Promise<void>
 *   structure - existing Structure row for edit mode, null for create
 *   saving  - boolean
 *   fieldErrors - array of Pydantic 422 error objects ({loc, msg})
 */
const StructureForm = ({ isOpen, onClose, onSave, structure, saving, fieldErrors = [] }) => {
    const [formData, setFormData] = useState(EMPTY_FORM);

    // Reset on every (re)open DURING RENDER, not in a useEffect. The component
    // stays mounted while closed (it returns null), so formData still holds the
    // last submission; an effect-based reset commits those stale values to the
    // DOM first and clears them a tick later, losing any input that lands in
    // between (see CircuitForm). Setting state while rendering makes React
    // re-render before committing, so stale values never reach the DOM.
    const openKey = isOpen ? `${structure?.id ?? 'new'}` : null;
    const [prevOpenKey, setPrevOpenKey] = useState(null);
    if (openKey !== prevOpenKey) {
        setPrevOpenKey(openKey);
        if (openKey !== null) {
            if (structure && structure.id) {
                setFormData({
                    structure_type: structure.structure_type || 'Roof',
                    name: structure.name || '',
                    material: structure.material || '',
                    install_date: structure.install_date || '',
                    warranty_expiration: structure.warranty_expiration || '',
                    contractor: structure.contractor || '',
                    notes: structure.notes || '',
                });
            } else {
                setFormData(EMPTY_FORM);
            }
        }
    }

    const isEdit = !!(structure && structure.id);

    const handleChange = (field, value) => {
        setFormData((prev) => ({ ...prev, [field]: value }));
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        if (!formData.structure_type) {
            toast.error('Structure type is required.');
            return;
        }
        const payload = {
            structure_type: formData.structure_type,
            name: formData.name.trim() || null,
            material: formData.material.trim() || null,
            install_date: formData.install_date || null,
            warranty_expiration: formData.warranty_expiration || null,
            contractor: formData.contractor.trim() || null,
            notes: formData.notes.trim() || null,
        };
        onSave(payload);
    };

    if (!isOpen) return null;

    const title = isEdit ? 'Edit Structure' : 'Add Structure';
    const labelClass = 'block text-xs font-semibold text-[var(--text-secondary)] uppercase mb-1 theme-transition';
    const inputClass = 'w-full p-3 rounded-lg bg-[var(--bg-hover)] border border-[var(--border)] text-[var(--text-main)] placeholder-[var(--text-muted)] focus:ring-2 focus:ring-[var(--primary)] focus:outline-none theme-transition';
    const suggestions = MATERIAL_SUGGESTIONS[formData.structure_type] || [];

    return (
        <Card className="p-5">
            <h4 className="text-sm font-bold text-[var(--text-main)] uppercase tracking-wide mb-3">
                {title}
            </h4>
            <form onSubmit={handleSubmit} aria-label={title} className="space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                        <label htmlFor="structure-type" className={labelClass}>
                            Type <span className="text-[var(--error)]">*</span>
                        </label>
                        <select
                            id="structure-type"
                            value={formData.structure_type}
                            onChange={(e) => handleChange('structure_type', e.target.value)}
                            className={inputClass}
                            required
                        >
                            {STRUCTURE_TYPES.map((t) => (
                                <option key={t} value={t}>{t}</option>
                            ))}
                        </select>
                    </div>
                    <div>
                        <label htmlFor="structure-name" className={labelClass}>Name</label>
                        <input
                            id="structure-name"
                            value={formData.name}
                            onChange={(e) => handleChange('name', e.target.value)}
                            placeholder="e.g. Main House Roof, Detached Garage"
                            className={inputClass}
                        />
                    </div>
                </div>

                <div>
                    <label htmlFor="structure-material" className={labelClass}>Material / Type</label>
                    <input
                        id="structure-material"
                        list="structure-material-suggestions"
                        value={formData.material}
                        onChange={(e) => handleChange('material', e.target.value)}
                        placeholder="e.g. Asphalt Shingle"
                        className={inputClass}
                    />
                    <datalist id="structure-material-suggestions">
                        {suggestions.map((s) => (
                            <option key={s} value={s} />
                        ))}
                    </datalist>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                        <label htmlFor="structure-install-date" className={labelClass}>Install Date</label>
                        <input
                            id="structure-install-date"
                            type="date"
                            value={formData.install_date}
                            onChange={(e) => handleChange('install_date', e.target.value)}
                            className={inputClass}
                        />
                    </div>
                    <div>
                        <label htmlFor="structure-warranty" className={labelClass}>Warranty Expiration</label>
                        <input
                            id="structure-warranty"
                            type="date"
                            value={formData.warranty_expiration}
                            onChange={(e) => handleChange('warranty_expiration', e.target.value)}
                            className={inputClass}
                        />
                    </div>
                </div>

                <div>
                    <label htmlFor="structure-contractor" className={labelClass}>Contractor</label>
                    <input
                        id="structure-contractor"
                        value={formData.contractor}
                        onChange={(e) => handleChange('contractor', e.target.value)}
                        placeholder="Who installed / built it"
                        className={inputClass}
                    />
                </div>

                <div>
                    <label htmlFor="structure-notes" className={labelClass}>Notes</label>
                    <textarea
                        id="structure-notes"
                        value={formData.notes}
                        onChange={(e) => handleChange('notes', e.target.value)}
                        placeholder="Anything else worth recording..."
                        rows={2}
                        className={`${inputClass} resize-none`}
                    />
                </div>

                <FieldErrorList errors={fieldErrors} />

                <div className="flex justify-end gap-2 pt-2">
                    <button
                        type="button"
                        onClick={onClose}
                        className="px-4 py-2 text-[var(--text-secondary)] hover:bg-[var(--bg-hover)] rounded-lg transition theme-transition"
                    >
                        Cancel
                    </button>
                    <button
                        type="submit"
                        disabled={saving}
                        className="px-4 py-2 bg-[var(--primary)] text-white rounded-lg font-medium hover:opacity-90 transition disabled:opacity-50"
                    >
                        {saving ? 'Saving...' : (isEdit ? 'Update' : 'Create')}
                    </button>
                </div>
            </form>
        </Card>
    );
};

export default StructureForm;
